import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { lessonsAPI, testsAPI } from '../services/api';
import { saveLesson } from '../utils/lessonUtils';
import { toast } from 'react-toastify';
import Button from '../components/UIKit/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LessonEditor from '../components/UIKit/LessonEditor';
import DeleteLessonDialog from '../components/DeleteLessonDialog';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfessions, selectAllProfessions, selectProfessionsLoading } from '../redux/slices/professionsSlice';
import { fetchSkills, selectAllSkills, selectSkillsLoading } from '../redux/slices/skillsSlice';

const UpdateLessonPage = () => {
  const { t } = useTranslation(['lessons', 'common']);
  const { lessonSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const dispatch = useDispatch();
  
  // Redux селекторы для профессий и навыков
  const professions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);
  const skills = useSelector(selectAllSkills);
  const skillsLoading = useSelector(selectSkillsLoading);
  
  // Получаем requestId из query параметров
  const searchParams = new URLSearchParams(location.search);
  const requestId = searchParams.get('requestId');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dependencies, setDependencies] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [isEditingFromRequest, setIsEditingFromRequest] = useState(false);
  // Проверяем, является ли пользователь админом или учителем
  const isAdmin = user?.userMeta?.roles?.includes('admin');
  const isTeacher = user?.userMeta?.roles?.includes('teacher');
  const isAdminOrTeacher = isAdmin || isTeacher;

  // Загружаем профессии и навыки при монтировании компонента
  useEffect(() => {
    if (professions.length === 0 && !professionsLoading) {
      dispatch(fetchProfessions({ all: true, isCommon: 1 }));
    }
    if (skills.length === 0 && !skillsLoading) {
      dispatch(fetchSkills({ all: true, isCommon: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Оптимизированная загрузка данных урока
  useEffect(() => {
    let isMounted = true;

    const fetchDataFromRequest = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsEditingFromRequest(true);

        const response = await lessonsAPI.getMyChangeRequestById(requestId);
        
        if (!isMounted) return;
        
        if (response?.data?.data) {
          const requestData = response.data.data;
          // Создаем урок с данными из запроса
          const lessonFromRequest = {
            // Дефолтные значения
            title: '',
            contentType: 'standard',
            content: '',
            duration: 1,
            type: 'text',
            videoUrl: '',
            contentBlocks: [],
            resources: [],
            practiceExercises: [],
            isDraft: false,
            professions: [],
            // Перезаписываем данными из урока и запроса
            ...requestData.lesson,
            ...requestData.proposedChanges,
            comment: requestData.comment || ''
          };
          setLesson(lessonFromRequest);
        } else {
          setError(t('lessons:page.errors.requestNotFound'));
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error loading change request:', err);
        setError(
          err.response?.data?.message || t('lessons:page.errors.requestLoadError')
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const fetchLesson = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsEditingFromRequest(false);

        const response = await lessonsAPI.getById(lessonSlug);
        
        if (!isMounted) return;
        
        if (response?.data?.data) {
          const lessonData = response.data.data;
          setLesson(lessonData);
          
          // Проверка прав доступа
          const isAuthor = lessonData.instructor?._id === user?._id;
          if (!isAdmin && !isTeacher && !isAuthor) {
            setError(t('common:errors.accessDenied'));
            setTimeout(() => {
              if (isMounted) {
                navigate(`/lessons/${lessonSlug}`);
              }
            }, 3000);
          }
        } else {
          setError(t('common:errors.notFound'));
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Ошибка при загрузке урока:', err);
        setError(
          err.response?.data?.message || t('common:errors.loadError')
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Выбираем функцию загрузки в зависимости от наличия requestId
    const loadData = requestId ? fetchDataFromRequest : fetchLesson;
    loadData();

    // Cleanup функция для предотвращения утечек памяти
    return () => {
      isMounted = false;
    };
  }, [lessonSlug, requestId, user, navigate, isAdmin, isTeacher, t, location.search]);

  const handleSaveLesson = async () => {
    try {
      setSaving(true);

      // Валидация обязательных полей
      if (!lesson?.title?.trim()) {
        toast.error(t('create:validation.titleRequired'));
        return;
      }

      if (isEditingFromRequest && requestId) {
        // Validate requestId
        if (!requestId || requestId === 'undefined' || requestId === 'null') {
          console.error('Invalid requestId:', requestId);
          toast.error('Invalid request ID');
          return;
        }
        
        // Оптимизированная структура для обновления запроса
        const updateData = {
          proposedChanges: {
            title: lesson.title,
            contentType: lesson.contentType,
            content: lesson.content,
            duration: lesson.duration,
            type: lesson.type,
            videoUrl: lesson.videoUrl,
            contentBlocks: lesson.contentBlocks || [],
            resources: lesson.resources || [],
            practiceExercises: lesson.practiceExercises || [],
            professions: lesson.professions || []
          },
          comment: lesson.comment?.trim() || ''
        };
        
        await lessonsAPI.updateMyChangeRequest(requestId, updateData);
        toast.success(t('lessons:page.requestUpdateSuccess'));
        navigate('/my-change-requests');
      } else {
        // Обычное сохранение урока с валидацией
        const validatedLesson = {
          ...lesson,
          title: lesson.title.trim(),
          content: lesson.content || '',
          contentBlocks: lesson.contentBlocks || [],
          resources: lesson.resources || [],
          practiceExercises: lesson.practiceExercises || [],
          professions: lesson.professions || [],
          tests: lesson.tests || []
        };
        
        await saveLesson(validatedLesson);
        
        // Если есть тесты, обновляем их по одному
        if (lesson.tests && lesson.tests.length > 0) {
          try {
            for (const testData of lesson.tests) {
              if (testData._id && !testData._id.startsWith('temp-')) {
                // Обновляем существующий тест с lessonId
                await testsAPI.update(testData._id, { ...testData, lessonId: lesson._id });
                console.log('Тест обновлен:', testData._id);
              } else {
                const { _id, ...testDataWithoutId } = testData;
                await testsAPI.create({ ...testDataWithoutId, lessonId: lesson._id });
                console.log('Тест создан для урока:', lesson._id);
              }
            }
            console.log('Все тесты успешно обработаны для урока:', lesson._id);
          } catch (testError) {
            console.error('Ошибка при обновлении тестов:', testError);
            toast.warning('Урок сохранен, но произошла ошибка при обновлении тестов');
          }
        }
        
        toast.success(t('editLesson.saveSuccess'));
        navigate(`/lessons/${lessonSlug}`);
      }
    } catch (err) {
      console.error('Ошибка при сохранении:', err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          t('common:errors.saveFailed');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleBackToLesson = () => {
    navigate(`/lessons/${lessonSlug}`);
  };

  const handleDeleteClick = async () => {
    try {
      // Проверяем зависимости урока с оптимизированным запросом
      const dependenciesPromise = Promise.allSettled([
        lessonsAPI.getCourses(lesson._id),
        lessonsAPI.getModules(lesson._id)
      ]);

      const [coursesResult, modulesResult] = await dependenciesPromise;
      
      const courses = coursesResult.status === 'fulfilled' 
        ? coursesResult.value?.data?.data || [] 
        : [];
      const modules = modulesResult.status === 'fulfilled' 
        ? modulesResult.value?.data?.data || [] 
        : [];

      // Устанавливаем зависимости только если есть данные
      const hasDependencies = courses.length > 0 || modules.length > 0;
      setDependencies(hasDependencies ? { courses, modules } : null);
      
      setDeleteDialogOpen(true);
    } catch (err) {
      console.error('Ошибка при проверке использования урока:', err);
      setDependencies(null);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await lessonsAPI.delete(lesson._id);
      toast.success(t('myLessons.success.deleted'));
      navigate('/lessons');
    } catch (err) {
      console.error('Ошибка при удалении урока:', err);
      
      // Оптимизированная обработка ошибок
      const errorCode = err.response?.data?.code;
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          t('myLessons.errors.deleteError');
      
      if (errorCode === 'LESSON_HAS_DEPENDENCIES') {
        toast.error(err.response.data.message);
      } else {
        toast.error(errorMessage);
      }
      
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDependencies(null);
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!lesson) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">{t('common:errors.notFound')}</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1">
          {t('editLesson.title')}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToLesson}
            sx={{ mr: 2 }}
          >
            {t('editLesson.buttons.back')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            sx={{ mr: 2 }}
          >
            {t('common:actions.delete')}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveLesson}
            disabled={saving}
          >
            {saving
              ? t('editLesson.buttons.saving')
              : t('editLesson.buttons.save')}
          </Button>
        </Box>
      </Box>

      <LessonEditor
        lesson={lesson}
        onChange={setLesson}
        isAdminOrTeacher={isAdminOrTeacher}
        header={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" component="h2">
              {lesson.title}
            </Typography>
          </Box>
        }
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 4,
        }}
      >
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveLesson}
          disabled={saving}
          size="large"
        >
          {saving
            ? t('editLesson.buttons.saving')
            : t('editLesson.buttons.save')}
        </Button>
      </Box>

      {/* Универсальный диалог удаления урока */}
      <DeleteLessonDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        lesson={lesson}
        dependencies={dependencies}
        loading={deleting}
      />
    </Container>
  );
};

export default UpdateLessonPage; 